import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import localeText from "../../../../locale";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import ProductService from "../../../../service/ProductService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useCategories from "../../../../hooks/useCategories";
import { locale } from "../../../../utilities/locale/locale";
import { resolveThemeImageUrl } from "../../../../utilities/resolveThemeImageUrl";
import { isReservedStoreSlug } from "../../../../utilities/storeSlug";
import StoreSettingsGeneralTab from "./components/StoreSettingsGeneralTab";
import StoreSettingsLocationTab from "./components/StoreSettingsLocationTab";
import StoreSettingsIntegrationsTab from "./components/StoreSettingsIntegrationsTab";
import "./StoreSettingsPage.css";
import StoreSettingsBusinessDiagnosis from "../BusinessDiagnosis/StoreSettingsBusinessDiagnosis";
import StoreSettingsChannelTab from "./components/StoreSettingsChannelTab";
import useUserType from "../../../../hooks/useUserType";

// "scontiposte" fica por ultimo: so aparece para Manager, sem mudar o indice das outras abas
const TAB_SLUGS = ["general", "location", "integrations", "businessDiagnosis", "scontiposte"];

const StoreSettingsPage = ({ userInfo }) => {
  const { userType } = useUserType();
  const location = useLocation();
  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToastContext();

  const getTabIndexFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const idx = TAB_SLUGS.indexOf(tab);
    return idx >= 0 ? idx : 0;
  };

  const [activeTabIndex, setActiveTabIndex] = useState(getTabIndexFromUrl);

  useEffect(() => {
    const idx = getTabIndexFromUrl();
    setActiveTabIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleTabChange = (e) => {
    const slug = TAB_SLUGS[e.index] ?? "general";
    history.replace({ search: `?tab=${slug}` });
  };

  const getUserInfoService = useMemo(() => new GetUserInfoService(), []);
  const productService = useMemo(() => new ProductService(), []);
  const logoInputRef = useRef(null);
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopConfig, setShopConfig] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [logoId, setLogoId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);

  useEffect(() => {
    if (logoFile) {
      const url = URL.createObjectURL(logoFile);
      setLogoBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setLogoBlobUrl(null);
  }, [logoFile]);

  const logoDisplayUrl = logoBlobUrl || (logoId ? resolveThemeImageUrl(String(logoId)) : null);
  const [siteLink, setSiteLink] = useState("");
  const [facebookLink, setFacebookLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [instagramLink, setInstagramLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [freightRule, setFreightRule] = useState(null);
  const [freightDiscount, setFreightDiscount] = useState(null);
  const [minimumOrderValue, setMinimumOrderValue] = useState(null);
  const [loadingFreight, setLoadingFreight] = useState(true);

  const handleSuccessCategories = (categories) => {
    const localeOptions = {
      en: "descriptionEnglish",
      it: "descriptionItalian",
      pt: "descriptionPortuguese",
      es: "descriptionSpanish",
    };
    const key = localeOptions[locale] || "descriptionEnglish";
    return {
      result: (categories || []).map((cat) => ({
        label: cat[key] ?? cat.description,
        value: { id: cat.id, label: cat[key] ?? cat.description },
      })),
    };
  };

  const { categories: categoriesOptions, loadingCategories } = useCategories({
    onSuccess: handleSuccessCategories,
    onCategoriesError: (code) => showErrorToast({ code }),
  });

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [initialCategoryIds, setInitialCategoryIds] = useState([]);
  const [pendingRemovedCategoryIds, setPendingRemovedCategoryIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getUserInfoService.getUserShop(username);
        if (cancelled) return;
        if (res?.messageCode === 200001 && res?.result?.shopConfiguration) {
          const c = res.result.shopConfiguration;
          setShopConfig(c);
          setTitle(c.title ?? "");
          setDescription(c.description ?? "");
          setSlug(c.slug ?? "");
          setLogoId(c.imagelogoid ?? null);
          setSiteLink(c.website ?? "");
          setFacebookLink(c.facebook ?? "");
          setTwitterLink(c.twitter ?? "");
          setInstagramLink(c.instagram ?? "");
          setYoutubeLink(c.youtube ?? "");
          setLinkedin(c.linkedin ?? "");
          setTiktok(c.tiktok ?? "");

          const languages = {
            en: "descriptionEnglish",
            it: "descriptionItalian",
            pt: "descriptionPortuguese",
            es: "descriptionSpanish",
          };

          const language = languages[locale];

          const cats = (c.categories ?? []).map((cat) => ({
            id: cat.id,
            label: cat[language],
          }));

          setSelectedCategories(cats);
          setInitialCategoryIds(cats.map((cat) => cat.id));
          setPendingRemovedCategoryIds([]);
        }
      } catch (e) {
        if (!cancelled) showErrorToast({ code: 500000 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [username, getUserInfoService, showErrorToast]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingFreight(true);
      try {
        const res = await getUserInfoService.getFreightRules();
        if (!cancelled && res?.result) {
          setFreightRule(res.result.freightRule ?? null);
          setFreightDiscount(res.result.freightDiscount ?? null);
          setMinimumOrderValue(res.result.minimumOrderValue ?? null);
        }
      } catch (e) {
        if (!cancelled) setLoadingFreight(false);
      } finally {
        if (!cancelled) setLoadingFreight(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [getUserInfoService]);

  const uploadLogoImage = async (file) => {
    if (!file || typeof file === "string") return null;
    const res = await productService.postUploadImage(token, file);
    if (res?.messageCode !== 200000) {
      showErrorToast(
        res?.messageCode ? { code: res.messageCode } : { detail: "unexpectedErrorUploadImg" },
      );
      return null;
    }
    return res?.result ?? null;
  };

  const handleSaveGeneral = async () => {
    if (!shopConfig?.id) return;

    // Slug vazio remove o link personalizado; um slug já salvo segue sem revalidar.
    const slugToSave = slug ? slug : null;
    if (slugToSave && slugToSave !== shopConfig.slug && isReservedStoreSlug(slugToSave)) {
      showErrorToast({ code: 500198 });
      return;
    }

    setSaving(true);
    try {
      let imageLogoId = logoId ?? shopConfig.imagelogoid;
      if (logoFile) {
        const uploaded = await uploadLogoImage(logoFile);
        if (uploaded) imageLogoId = uploaded;
      }
      const res = await getUserInfoService.postEditShop(
        token,
        username,
        title,
        description,
        imageLogoId,
        instagramLink,
        twitterLink,
        facebookLink,
        youtubeLink,
        siteLink,
        linkedin,
        tiktok,
        slugToSave,
        shopConfig.video ?? null,
      );
      if (res?.messageCode !== 200000) {
        showErrorToast({ code: res?.messageCode });
        setSaving(false);
        return;
      }
      if (selectedCategories?.length > 0) {
        await getUserInfoService.linkCategoryToStore(
          token,
          selectedCategories.map((c) => c?.id ?? c),
          shopConfig.id,
        );
      }

      if (pendingRemovedCategoryIds.length > 0) {
        const deleteResponses = await Promise.all(
          pendingRemovedCategoryIds.map((categoryId) =>
            getUserInfoService.unlinkCategoryToStore(token, shopConfig.id, categoryId),
          ),
        );

        const hasDeleteError = deleteResponses.some((resp) => resp?.messageCode !== 200003);

        if (hasDeleteError) {
          showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
          setSaving(false);
          return;
        }
      }

      showSuccessToast({ detail: "storeEditedSuccessfully" });
      setLogoId(imageLogoId);
      setLogoFile(null);
      setInitialCategoryIds(selectedCategories.map((cat) => cat.id));
      setPendingRemovedCategoryIds([]);
      if (logoInputRef.current) logoInputRef.current.value = "";
      setShopConfig((prev) =>
        prev ? { ...prev, title, description, slug: slugToSave, imagelogoid: imageLogoId } : null,
      );
    } catch (e) {
      showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLocation = async () => {
    setSaving(true);
    try {
      const res = await getUserInfoService.postEditShop(
        token,
        username,
        shopConfig?.title ?? title,
        shopConfig?.description ?? description,
        shopConfig?.imagelogoid ?? logoId,
        instagramLink,
        twitterLink,
        facebookLink,
        youtubeLink,
        siteLink,
        linkedin,
        tiktok,
        shopConfig?.slug ?? null,
        shopConfig?.video ?? null,
      );
      if (res?.messageCode !== 200000) {
        showErrorToast({ code: res?.messageCode });
        setSaving(false);
        return;
      }
      const freightRes = await getUserInfoService.postFreightRule({
        freightRule: freightRule ?? undefined,
        freightDiscount: freightDiscount ?? undefined,
        minimumOrderValue: minimumOrderValue ?? undefined,
      });
      if (freightRes?.messageCode === 200000 || freightRes?.messageCode === 200001) {
        showSuccessToast({ detail: "successSaveFreightRule" });
      } else {
        showErrorToast({ code: freightRes?.messageCode });
      }
    } catch (e) {
      showErrorToast({ code: 500000 });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCategory = (category) => {
    if (!category?.id) return;

    setSelectedCategories((prev) => prev.filter((c) => c.id !== category.id));

    if (initialCategoryIds.includes(category.id)) {
      setPendingRemovedCategoryIds((prev) =>
        prev.includes(category.id) ? prev : [...prev, category.id],
      );
    }
  };

  const handleAddCategory = (category) => {
    if (!category?.id) return;

    setPendingRemovedCategoryIds((prev) => prev.filter((id) => id !== category.id));
  };

  if (loading && !shopConfig) {
    return (
      <DashboardLayout>
        <StoreBreadcrumbs />
        <div className="flex justify-content-center align-items-center p-8">
          <i className="pi pi-spin pi-spinner text-4xl text-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      <div className="flex align-items-center gap-4 mb-5 mt-4">
        <div
          className="border-circle p-3"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            width: "54px",
            height: "54px",
          }}
        >
          <i className="pi pi-cog text-xl" style={{ color: "var(--theme-text-muted, #5C5C5C)" }} />
        </div>
        <div>
          <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
            {localeText("storeSettings")}
          </h2>
          <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("storePanelSubtitle")}
          </p>
        </div>
      </div>

      <TabView
        className="store-settings-tabview"
        activeIndex={activeTabIndex}
        onTabChange={handleTabChange}
      >
        <TabPanel header={localeText("settingsTabGeneral")}>
          <StoreSettingsGeneralTab
            logoDisplayUrl={logoDisplayUrl}
            onLogoFileChange={setLogoFile}
            onLogoInputRef={(el) => {
              logoInputRef.current = el;
            }}
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            slug={slug}
            setSlug={setSlug}
            savedSlug={shopConfig?.slug}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            onAddCategory={handleAddCategory}
            onRemoveCategory={handleRemoveCategory}
            categoriesOptions={categoriesOptions}
            loadingCategories={loadingCategories}
            onSave={handleSaveGeneral}
            saving={saving}
            showErrorToast={showErrorToast}
          />
        </TabPanel>
        <TabPanel header={localeText("settingsTabLocationContact")}>
          <StoreSettingsLocationTab
            siteLink={siteLink}
            setSiteLink={setSiteLink}
            facebookLink={facebookLink}
            setFacebookLink={setFacebookLink}
            twitterLink={twitterLink}
            setTwitterLink={setTwitterLink}
            instagramLink={instagramLink}
            setInstagramLink={setInstagramLink}
            youtubeLink={youtubeLink}
            setYoutubeLink={setYoutubeLink}
            linkedin={linkedin}
            setLinkedin={setLinkedin}
            tiktok={tiktok}
            setTiktok={setTiktok}
            freightRule={freightRule}
            setFreightRule={setFreightRule}
            freightDiscount={freightDiscount}
            setFreightDiscount={setFreightDiscount}
            minimumOrderValue={minimumOrderValue}
            setMinimumOrderValue={setMinimumOrderValue}
            loadingFreight={loadingFreight}
            onSave={handleSaveLocation}
            saving={saving}
          />
        </TabPanel>
        <TabPanel header={localeText("settingsTabIntegrations")}>
          <StoreSettingsIntegrationsTab userInfo={userInfo} />
        </TabPanel>
        <TabPanel header={localeText("businessDiagnosis")}>
          <StoreSettingsBusinessDiagnosis />
        </TabPanel>
        {userType === "Manager" && (
          <TabPanel header={localeText("settingsTabScontiPoste")}>
            <StoreSettingsChannelTab />
          </TabPanel>
        )}
      </TabView>
    </DashboardLayout>
  );
};

export default StoreSettingsPage;
